import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const DigestPage = () => {
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      setLoading(false);
      setError('signin');
      return;
    }
    
    if (user.tier !== 'pro') {
      setLoading(false);
      setError('upgrade');
      return;
    }

    // Fetch digest for pro users
    axios.get(`${API_URL}/v1/digest`, { withCredentials: true })
      .then(res => {
        setDigest(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Failed to load digest');
        setLoading(false);
      });
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <>
        <Header />
        <main className="main page-content">
          <div className="digest-page">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading digest...</p>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Not signed in
  if (error === 'signin') {
    return (
      <>
        <Header />
        <main className="main page-content">
          <div className="digest-page">
            <div className="locked-feature">
              <div className="lock-icon">🔒</div>
              <h1>Daily Digest</h1>
              <p>Sign in to access your personalized daily digest.</p>
              <button className="action-btn primary" onClick={login} data-testid="signin-for-digest">
                Sign In with Google
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Free user - show upgrade prompt
  if (error === 'upgrade') {
    return (
      <>
        <Header />
        <main className="main page-content">
          <div className="digest-page">
            <div className="locked-feature">
              <div className="lock-icon">🔒</div>
              <h1>Daily Digest</h1>
              <p className="feature-description">
                Get a curated summary of the day's most interesting framing differences, 
                delivered to your inbox every morning.
              </p>
              <div className="upgrade-card">
                <h3>Pro Feature</h3>
                <p>Upgrade to Framecast Pro to unlock:</p>
                <ul>
                  <li>📧 Daily digest emails</li>
                  <li>📚 Full archive access</li>
                  <li>⚡ Priority support</li>
                </ul>
                <div className="price-tag">Starting at $1/week</div>
                <button className="action-btn primary" onClick={() => navigate('/subscribe')} data-testid="upgrade-for-digest">
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Pro user - show digest
  return (
    <>
      <Header />
      <main className="main page-content">
        <div className="digest-page">
          <div className="digest-header">
            <h1>Daily Digest</h1>
            <p className="digest-date">{digest?.date}</p>
          </div>
          <div className="digest-stories">
            {digest?.stories?.map((story, i) => (
              <div key={i} className="digest-story-card">
                <h3>{story.title}</h3>
                <p className="framing-insight">{story.framing_insight}</p>
                <div className="story-meta">
                  <span>{story.source_count} sources</span>
                  <span>{story.regions?.length} regions</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default DigestPage;
